import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import {
  Button,
  CardActions,
  CardContent,
  Checkbox, FormControlLabel, Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import _ from 'lodash';
import ReadOnlyQuillEditor from '../TextEditors/ReadOnlyQuillEditor';
import { ISSUE_TYPE, QUESTION_TYPE, REPORT_TYPE, SUGGEST_CHANGE_TYPE, TODO_TYPE, } from '../../constants/comments';
import { OperationInProgressContext } from '../../contexts/OperationInProgressContext/OperationInProgressContext';
import { usePresences } from '../../contexts/MarketPresencesContext/marketPresencesHelper';
import {
  addCommentToMarket, getComment, getCommentRoot, getCommentThreads, getMarketComments, moveToDiscussion
} from '../../contexts/CommentsContext/commentsContextHelper';
import { CommentsContext } from '../../contexts/CommentsContext/CommentsContext';
import UsefulRelativeTime from '../TextFields/UseRelativeTime';
import {
  decomposeMarketPath,
  formCommentLink, formMarketAddInvestibleLink,
  formWizardLink,
  navigate,
  preventDefaultAndProp
} from '../../utils/marketIdPathFunctions';
import { useHistory, useLocation } from 'react-router';
import { NotificationsContext } from '../../contexts/NotificationsContext/NotificationsContext';
import { findMessageForCommentId } from '../../utils/messageUtils';
import { onCommentsMove } from '../../utils/commentFunctions';
import { invalidEditEvent } from '../../utils/windowUtils';
import TooltipIconButton from '../Buttons/TooltipIconButton';
import NotificationMenuButton from '../Buttons/NotificationMenuButton';
import MenuButton from '../Buttons/MenuButton';
import { ScrollContext } from '../../contexts/ScrollContext';
import { EditCommentContext } from '../../contexts/EditCommentContext/EditCommentContext';
import ListAltIcon from '@material-ui/icons/ListAlt';
import { LocalCommentsContext, useCommentStyles } from './Comment';
import { stripHTML } from '../../utils/stringFunctions';
import Gravatar from '../Avatars/Gravatar';
import NotificationDeletion from '../../pages/Home/YourWork/NotificationDeletion';
import {
  BUG_WIZARD_TYPE,
  DECISION_TYPE,
  DELETE_COMMENT_TYPE,
  IN_PROGRESS_WIZARD_TYPE,
  PLANNING_TYPE,
  REPLY_WIZARD_TYPE
} from '../../constants/markets';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { hasReply } from '../AddNewWizards/Reply/ReplyStep';
import { useInlineWizardLaunch } from '../InlineWizard/InlineWizardContext';
import EditIcon from '@material-ui/icons/Edit';
import { MarketsContext } from '../../contexts/MarketsContext/MarketsContext';
import { getMarket } from '../../contexts/MarketsContext/marketsContextHelper';
import { Done, Edit, Eject, NotificationsActive } from '@material-ui/icons';
import { moveComments, updateComment } from '../../api/comments';
import { previousInProgress } from '../AddNewWizards/TaskInProgress/TaskInProgressWizard';
import { getNotDoingStage } from '../../contexts/MarketStagesContext/marketStagesContextHelper';
import { InvestiblesContext } from '../../contexts/InvestibesContext/InvestiblesContext';
import { MarketStagesContext } from '../../contexts/MarketStagesContext/MarketStagesContext';
import { GroupMembersContext } from '../../contexts/GroupMembersContext/GroupMembersContext';
import { isMyPokableComment } from '../../pages/Home/YourWork/InboxExpansionPanel';
import { getMarketClient } from '../../api/marketLogin';
import { ACTION_BUTTON_COLOR, DARK_TEXT_BACKGROUND_COLOR } from '../Buttons/ButtonConstants';
import { ThemeModeContext } from '../../contexts/ThemeModeContext';
import InvesibleCommentLinker from '../../pages/Dialog/InvesibleCommentLinker';
import { BLUE_LEVEL } from '../../constants/notifications';
import PokeAIButton, { isPokeAIReplyVisible } from '../Buttons/PokeAIButton';

const useReplyStyles = makeStyles(
  theme => {
    return {
      actions: {
        display: "flex",
        boxShadow: "none",
        width: "100%"
      },
      action: {
        padding: "0 4px",
        minWidth: "20px",
        height: "20px",
        color: "#A7A7A7",
        fontWeight: "bold",
        fontSize: 12,
        lineHeight: "18px",
        textTransform: "capitalize",
        background: "transparent",
        borderRight: "none !important",
        "&:hover": {
          color: "#ca2828",
          background: "white",
          boxShadow: "none"
        },
        display: "inline-block"
      },
      cardContent: {
        marginLeft: theme.spacing(1),
        paddingTop: 0,
        paddingRight: 0,
        paddingLeft: 0,
        paddingBottom: "0.5rem",
      },
      cardActions: {
        marginLeft: theme.spacing(1),
        padding: 0
      },
      cardActionsYellow: {
        marginLeft: theme.spacing(3),
        boxShadow: "4px 4px 4px yellow",
        padding: 0
      },
      cardActionsRed: {
        marginLeft: theme.spacing(3),
        boxShadow: "4px 4px 4px red",
        padding: 0
      },
      commenter: {
        color: "#7E7E7E",
        display: "inline-block",
        fontSize: 14,
        fontWeight: "bold",
        marginRight: "8px",
        paddingBottom: '0.5rem'
      },
      replyContainer: {
        marginLeft: "6px",
        [theme.breakpoints.down('sm')]: {
          marginLeft: '3px',
        }
      },
      timeElapsed: {
        color: theme.palette.type === 'dark' ? '#8E8E8E' : '#A7A7A7',
        display: "inline-block",
        fontSize: 14
      },
      timePosted: {
        color: "#A7A7A7",
        display: "inline-block",
        fontSize: 12,
        fontWeight: "bold"
      },
      container: {
        marginTop: '1.5rem',
        marginRight: '0.25rem',
        overflow: 'unset'
      },
      containerLink: {
        marginTop: '1.5rem',
        marginRight: '0.25rem',
        overflow: 'unset',
        cursor: 'pointer'
      },
      rootCheckbox: {
        color: 'black',
        opacity: 0.54,
      },
      checkedCheckbox: {
        opacity: 1,
      },
      containerBlueLink: {
        boxShadow: "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px blue",
        overflow: 'unset',
        marginTop: "1.5rem",
        marginRight: '0.25rem',
        paddingRight: '0.5rem',
        cursor: 'pointer'
      },
      containerBlueLinkCompressed: {
        boxShadow: "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px blue",
        overflow: 'unset',
        marginRight: '0.25rem',
        paddingRight: '0.5rem',
        cursor: 'pointer'
      },
      containerYellow: {
        marginTop: '1.5rem',
        boxShadow: "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px blue",
        marginRight: '0.25rem',
        paddingRight: '0.5rem',
        overflow: 'unset'
      },
      containerHashYellow: {
        marginTop: '1.5rem',
        backgroundColor: "#FBF6D8",
        marginRight: '0.25rem',
        paddingRight: '0.5rem',
        overflow: 'unset'
      },
      containerRed: {
        marginTop: '1.5rem',
        boxShadow: "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px red",
        marginRight: '0.25rem',
        paddingRight: '0.5rem',
        overflow: 'unset'
      }
    };
  },
  { name: "Reply" }
);

function Reply(props) {
  const { comment, enableEditing, replyEditId, inboxMessageId, isInbox, wizardProps, useCompression,
    toggleCompression, myPresenceIsAssigned, enableActions, isDeletable } = props;
  const history = useHistory();
  const location = useLocation();
  const replyBeingEdited = replyEditId === comment.id && isInbox;
  const [themeMode] = React.useContext(ThemeModeContext);
  const isDark = themeMode === 'dark';
  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    marketId,
    idPrepend,
    pokeAIMarketId,
    pokeAIParentTicketCode
  } = React.useContext(LocalCommentsContext);
  const { openInlineWizard } = useInlineWizardLaunch();
  const presences = usePresences(marketId);
  const commenter = useCommenter(comment, presences) || { name: "unknown", email: "" };
  const [hashFragment, noHighlightId, setNoHighlightId] = useContext(ScrollContext);
  const { editComment, openEditComment, closeEditComment } = useContext(EditCommentContext);
  const [messagesState, messagesDispatch] = useContext(NotificationsContext);
  const [commentsState, commentsDispatch] = useContext(CommentsContext);
  const [operationRunning, setOperationRunning] = useContext(OperationInProgressContext);
  const [marketsState] = useContext(MarketsContext);
  const [investiblesState] = useContext(InvestiblesContext);
  const [marketStagesState] = useContext(MarketStagesContext);
  const [groupPresencesState] = useContext(GroupMembersContext);
  const { pathname } = location;
  const { marketId: typeObjectIdRaw, action } = decomposeMarketPath(pathname);
  const typeObjectId = action === 'inbox' ? typeObjectIdRaw : undefined;
  const myMessage = findMessageForCommentId(comment.id, messagesState.messages) || {};
  const myPresence = presences.find(presence => presence.current_user) || {};
  const userId = myPresence?.id;
  const isEditable = comment.created_by === userId;
  const classes = useReplyStyles();
  const commentClasses = useCommentStyles();
  const rootComment = getCommentRoot(commentsState, marketId, comment.id);
  const parentComment = getComment(commentsState, marketId, comment.reply_id);
  const market = getMarket(marketsState, marketId);
  const { investible_id: investibleId, group_id: groupId } = comment || {};
  // Can make a task out of any reply, even when the root comment is resolved
  const showConvert = investibleId && [REPORT_TYPE, TODO_TYPE, ISSUE_TYPE, QUESTION_TYPE, SUGGEST_CHANGE_TYPE].includes(rootComment?.comment_type)
    && !isInbox && market?.market_type !== DECISION_TYPE && rootComment?.notification_type !== BLUE_LEVEL;
  const isSubTask = market?.market_type === PLANNING_TYPE && rootComment?.comment_type === TODO_TYPE && investibleId &&
    (comment.created_by === rootComment?.created_by || comment.notification_type === BLUE_LEVEL);
  const isTopLevelSubTask = isSubTask && rootComment?.id === comment.reply_id;
  const isMySubTask = isTopLevelSubTask && rootComment?.created_by === userId;
  const inProgress = comment.in_progress;
  // Mine if I wrote it under my own note, or it's a BLUE note reply I made on someone else's note
  // via the Add Note button (T-all-2247) - same BLUE test isSubTask uses above.
  const thisIsMyNote = comment.created_by === userId && rootComment?.notification_type === BLUE_LEVEL
   && rootComment?.comment_type === REPORT_TYPE && (parentComment?.created_by ===  userId
    || comment.notification_type === BLUE_LEVEL);

  function handleEditClick(event) {
    preventDefaultAndProp(event);
    const id = comment.id;
    if (replyEditId) {
      // T-all-2209 / Q-all-156 O-2: close the top-level edit modal if this reply is
      // open in it; otherwise fall back to the standalone /comment page navigation.
      if (editComment?.commentId === id && closeEditComment) {
        closeEditComment();
      } else {
        navigate(history, formCommentLink(marketId, groupId, investibleId, id));
      }
    } else {
      setNoHighlightId(id);
      if (openEditComment) {
        openEditComment(marketId, id);
      } else {
        navigate(history, `/comment/${marketId}/${id}`, false, true);
      }
    }
  }

  function setBeingEdited(event) {
    if (mobileLayout || invalidEditEvent(event)) {
      return;
    }
    handleEditClick(event);
  }

  function setReplyOpen() {
    navigate(history, formWizardLink(REPLY_WIZARD_TYPE, marketId, undefined, undefined, comment.id,
      typeObjectId));
  }
  const isLinkedTo = noHighlightId !== comment.id && hashFragment?.includes(comment.id);
  const intl = useIntl();
  function getHighlightClass() {
    if (isInbox) {
      if (inboxMessageId) {
        if (inboxMessageId === comment.id) {
          if (useCompression) {
            return classes.containerBlueLinkCompressed;
          }
          return classes.containerBlueLink;
        }
      }
      return classes.containerLink;
    }
    if (isLinkedTo) {
      return classes.containerHashYellow;
    }
    return classes.container;
  }

  function moveToTask() {
    setOperationRunning(true);
    return updateComment({marketId, commentId: comment.id, commentType: TODO_TYPE}).then((taskComment) => {
      addCommentToMarket(taskComment, commentsState, commentsDispatch);
      setOperationRunning(false);
      navigate(history, formCommentLink(marketId, taskComment.group_id, taskComment.investible_id, taskComment.id));
    });
  }

  // J-all-392 / T-all-2453: the reply Move button carries BugDecisionStep's
  // choices so the wizard opens past that step; the separate Make task /
  // Ungroup button folds in as the Task item per S-all-227.
  const replyMoveBugLink = `${formMarketAddInvestibleLink(marketId, groupId, undefined, undefined,
    BUG_WIZARD_TYPE)}&fromCommentId=${comment.id}`;
  const replyMoveItems = [
    {
      id: `moveTask${comment.id}`,
      label: <FormattedMessage id={isTopLevelSubTask ? 'ungroupLabel' : 'TaskLabel'} />,
      onClick: () => {
        if (myPresenceIsAssigned) {
          return moveToTask();
        }
        navigate(history, `${replyMoveBugLink}&useType=Task`);
      }
    },
    {
      id: `moveSuggestion${comment.id}`,
      label: <FormattedMessage id='SuggestionLabel' />,
      onClick: () => navigate(history, `${replyMoveBugLink}&useType=Suggestion`)
    },
    {
      id: `moveDiscussion${comment.id}`,
      label: <FormattedMessage id='DiscussionLabel' />,
      onClick: () => {
        setOperationRunning(true);
        return moveToDiscussion(comment, commentsState, commentsDispatch, setOperationRunning, history);
      }
    },
    {
      id: `moveBug${comment.id}`,
      label: <FormattedMessage id='BugLabel' />,
      onClick: () => navigate(history, `${replyMoveBugLink}&useType=Bug`)
    }
  ];

  function handleToggleInProgress() {
    setOperationRunning(`inProgressCheckbox${comment.id}`);
    const notDoingStage = getNotDoingStage(marketStagesState, marketId) || {};
    const otherInProgress = previousInProgress(userId, comment, investiblesState, commentsState, notDoingStage.id);
    const sendToWizard = !inProgress && !_.isEmpty(otherInProgress);
    return updateComment({marketId, commentId: comment.id, inProgress: !inProgress}).then((comment) => {
      setOperationRunning(false);
      addCommentToMarket(comment, commentsState, commentsDispatch)
      if (sendToWizard) {
        navigate(history, formWizardLink(IN_PROGRESS_WIZARD_TYPE, marketId, undefined, undefined,
          comment.id));
      }
    });
  }

  function isNoNotification() {
    return  _.isEmpty(comment.mentions) && parentComment?.created_by === userId;
  }

  if (!marketId) {
    return React.Fragment;
  }

  // B-all-559: every comment displayed in an inbox wizard gets the same direct navigation
  // affordance as a root comment; expansion remains a separate explicit action.
  function openComment(event) {
    preventDefaultAndProp(event);
    navigate(history, formCommentLink(marketId, groupId, investibleId, comment.id));
  }

  const compressedCommentCard = <div style={{
    display: 'flex', alignItems: 'flex-start', paddingBottom: '0.5rem', paddingLeft: '0.5rem',
    paddingTop: '0.5rem', paddingRight: '0.5rem', cursor: 'pointer', backgroundColor: 'white'
  }} onClick={isInbox ? openComment : toggleCompression}>
    <Gravatar name={commenter.name} email={commenter.email} className={commentClasses.smallGravatar}/>
    <div className={commentClasses.compressedComment}>{stripHTML(comment.body)}</div>
    {isInbox && (
      <TooltipIconButton
        lightSurface
        icon={<OpenInNewIcon htmlColor={theme.palette.type === 'dark' ? 'black' : '#2F80ED'} />}
        onClick={openComment}
        size="small"
        noPadding
        translationId="rowOpenComment"
      />
    )}
    <div style={{ marginRight: '1rem' }}>
      <TooltipIconButton
        lightSurface
        icon={<ExpandMoreIcon />}
        onClick={(event) => {
          preventDefaultAndProp(event);
          toggleCompression();
        }}
        size="small"
        noPadding
        translationId="rowExpandComment"
      />
    </div>
  </div>;

  const commentCard = <div className={getHighlightClass()}
                            style={{width: !enableEditing ? 'fit-content' : undefined, paddingRight: '1rem', borderRadius: '8px',
                              backgroundColor: isLinkedTo ? undefined : (isDark ? DARK_TEXT_BACKGROUND_COLOR : 'white')}}
                            id={`${isInbox ? 'inbox' : ''}${idPrepend}${comment.id}`}>
    <div onClick={(event) => {
      if (!invalidEditEvent(event)&&(replyBeingEdited || isInbox)) {
        navigate(history, formCommentLink(marketId, groupId, investibleId, comment.id));
      }
    }}>
      <CardContent className={classes.cardContent}>
        {isTopLevelSubTask && (
          <Tooltip key='subTaskTipKey'
                   title={<FormattedMessage id='commentGroupedTaskLabel' />}>
            <div className={classes.commenter}>
              <ListAltIcon htmlColor={ACTION_BUTTON_COLOR} style={{height: 16, width: 16, transform: 'translateY(3px)'}} />
            </div>
          </Tooltip>
        )}
        <div style={{display: 'inline-block', marginRight: '1rem', marginTop: '-0.6rem'}}>
          <InvesibleCommentLinker commentId={comment.id} investibleId={investibleId} marketId={marketId} flushBottom 
            textColor='black' hideLink />
        </div>
        {!isTopLevelSubTask && (
          <Typography className={classes.commenter} variant="body2">
            {commenter.name}
          </Typography>
        )}
        {mobileLayout && (
          <Typography className={classes.timeElapsed} variant="body2">
            {intl.formatDate(comment.updated_at)}
          </Typography>
        )}
        {!mobileLayout && (
          <Typography className={classes.timeElapsed} variant="body2">
            Created <UsefulRelativeTime value={comment.created_at}/>.
            {comment.created_at < comment.updated_at && (
              <> Updated <UsefulRelativeTime value={comment.updated_at}/>.</>
            )}
          </Typography>
        )}
        {isInbox && (
          <TooltipIconButton
            lightSurface
            icon={<OpenInNewIcon htmlColor={theme.palette.type === 'dark' ? 'black' : '#2F80ED'} />}
            onClick={openComment}
            size="small"
            noPadding
            translationId="rowOpenComment"
          />
        )}
        {myMessage?.type_object_id && !isInbox && !replyBeingEdited && (
          <NotificationMenuButton lightSurface direct message={myMessage}
                                  unhighlightedColor={ACTION_BUTTON_COLOR}
                                  iconStyle={{marginLeft: '1rem'}} />
        )}
        {enableActions && isDeletable && (
          <TooltipIconButton
            lightSurface
            disabled={operationRunning !== false}
            onClick={(event) => {
                preventDefaultAndProp(event);
                // J-all-325 (T-all-2197): open delete inline when inside a container that supports it.
                if (openInlineWizard && !isInbox) {
                  openInlineWizard({ wizardType: DELETE_COMMENT_TYPE, marketId, commentId: comment.id });
                } else if (isInbox) {
                  navigate(history, `${deleteWizardBaseLink}&isInbox=${isInbox}`);
                } else {
                  navigate(history, deleteWizardBaseLink);
                }
              }}
            icon={<NotificationDeletion height={22} width={22} isRed />}
            size='small'
            translationId="commentRemoveLabel"
            doFloatRight
          />
        )}
        {isMyPokableComment(comment, presences, groupPresencesState, marketId) && !isNoNotification() && enableEditing && isEditable && !isSubTask && (
          <TooltipIconButton
            lightSurface
            disabled={operationRunning !== false}
            onClick={(event) => {
              preventDefaultAndProp(event);
              setOperationRunning(true);
              return getMarketClient(marketId).then((client) => client.users.pokeComment(comment.id).then(() => setOperationRunning(false)));
            }}
            icon={<NotificationsActive fontSize='small' htmlColor={ACTION_BUTTON_COLOR} /> }
            size='small'
            translationId='poke'
            doFloatRight
          />
        )}
        {isPokeAIReplyVisible(
          isTopLevelSubTask,
          pokeAIMarketId,
          pokeAIParentTicketCode
        ) &&
          enableEditing && !comment.resolved && !!comment.ticket_code && (
          <PokeAIButton
            marketId={pokeAIMarketId || marketId}
            ticketCode={comment.ticket_code}
            parentTicketCode={pokeAIParentTicketCode}
            id={`pokeAI${comment.id}`}
            iconOnly
            lightSurface
          />
        )}
        {/* Not just mobile - desktop only had the undiscoverable click-body-to-edit, so show the
            pencil on replies everywhere like top level comments do (T-all-2246 follow-on). */}
        {enableEditing && isEditable && (
          <TooltipIconButton
            lightSurface
            onClick={handleEditClick}
            icon={<Edit fontSize='small' htmlColor={ACTION_BUTTON_COLOR} />}
            size='small'
            translationId="edit"
            doFloatRight
          />
        )}
        {showConvert && (
          <MenuButton items={replyMoveItems}>
            <TooltipIconButton
              lightSurface
              disabled={operationRunning !== false}
              icon={<Eject fontSize='small' htmlColor={ACTION_BUTTON_COLOR} /> }
              size='small'
              translationId='storyFromComment'
              doFloatRight
            />
          </MenuButton>
        )}
        {showConvert && isTopLevelSubTask && !rootComment?.resolved && (
          <TooltipIconButton
            lightSurface
            disabled={operationRunning !== false}
            onClick={(event) => {
              preventDefaultAndProp(event);
              setOperationRunning(true);
              // B-all-552: the backend 403s resolve of a reply, so send the move that
              // promotes to a top level task and resolves in one update
              const movingComments = getCommentThreads([comment],
                getMarketComments(commentsState, marketId, groupId));
              return moveComments(marketId, investibleId, [comment.id], [comment.id], [comment.id])
                .then((movedComments) => {
                  onCommentsMove([comment.id], messagesState, movingComments, investibleId,
                    commentsDispatch, marketId, movedComments, messagesDispatch);
                  setOperationRunning(false);
                });
            }}
            icon={<Done fontSize='small' htmlColor={ACTION_BUTTON_COLOR} />}
            size='small'
            id={`subTaskResolve${comment.id}`}
            translationId="commentResolveLabel"
            doFloatRight
          />
        )}
        {showConvert && (
          <div style={{marginRight: '1rem'}} />
        )}
        {!_.isEmpty(comment) && (
          <div style={{marginLeft: '0.8rem', marginBottom: '0.5rem'}}>
            <ReadOnlyQuillEditor
              value={comment.body}
              id={comment.id}
              setBeingEdited={setBeingEdited}
              isEditable={!mobileLayout && enableEditing && isEditable}
            />
          </div>
        )}
      </CardContent>
      <CardActions className={classes.cardActions}>
        {enableEditing && !isMySubTask && (
          <Button
            className={classes.action}
            id={`commentReplyButton${comment.id}`}
            onClick={(event) => {
              preventDefaultAndProp(event);
              setReplyOpen();
            }}
            variant="text"
          >
            {intl.formatMessage({ id: thisIsMyNote ? "addNote" : "issueReplyLabel" })} {hasReply(comment) && <EditIcon htmlColor={ACTION_BUTTON_COLOR} style={{fontSize: '1rem'}} fontSize='small' />}
          </Button>
        )}
        {enableEditing && isTopLevelSubTask && (
          <FormControlLabel
            id='inProgressCheckbox'
            style={{maxHeight: '0.7rem', marginTop: '0.35rem', marginBottom: '0.35rem', color: 'black'}}
            control={
              <Checkbox
                size='small'
                id={`inProgressCheckbox${comment.id}`}
                checked={operationRunning === `inProgressCheckbox${comment.id}` ? !inProgress : inProgress}
                classes={{
                  root: classes.rootCheckbox,
                  checked: classes.checkedCheckbox,
                }}
                onClick={handleToggleInProgress}
                disabled={!myPresenceIsAssigned || operationRunning !== false}
              />
            }
            label={mobileLayout ? undefined : intl.formatMessage({ id: 'inProgress' })}
          />
        )}
      </CardActions>
    </div>
  </div>;

  if (useCompression && comment.id !== inboxMessageId) {
    return (
      <>
        {compressedCommentCard}
        <div className={classes.cardContent}>
          <ThreadedReplies
            replies={comment.children}
            enableEditing={enableEditing}
            enableActions={enableActions}
            isDeletable={isDeletable}
            replyEditId={replyEditId}
            isInbox={isInbox}
            useCompression={useCompression}
            inboxMessageId={inboxMessageId}
            myPresenceIsAssigned={myPresenceIsAssigned}
            wizardProps={wizardProps}
          />
        </div>
      </>
    );
  }

  const deleteWizardBaseLink = formWizardLink(DELETE_COMMENT_TYPE, marketId, undefined,
    undefined, comment.id);

  return (
    <>
      {commentCard}
      <div className={classes.replyContainer}>
        {comment.children !== undefined && (
          <div className={classes.cardContent}>
            <ThreadedReplies
              replies={comment.children}
              enableEditing={enableEditing}
              enableActions={enableActions}
              isDeletable={isDeletable}
              replyEditId={replyEditId}
              isInbox={isInbox}
              inboxMessageId={inboxMessageId}
              myPresenceIsAssigned={myPresenceIsAssigned}
              wizardProps={wizardProps}
            />
          </div>
        )}
      </div>
    </>
  );
}
Reply.propTypes = {
  comment: PropTypes.object.isRequired
};

const useThreadedReplyStyles = makeStyles(
  {
    container: {
      borderLeft: "2px solid #ADADAD",
      listStyle: "none",
      margin: 0,
      marginTop: 15,
      padding: 0
    },
    visible: {
      overflow: 'visible'
    }
  },
  { name: "ThreadedReplies" }
);
/**
 *
 * @param {{comments: Comment[], replies: string[]}} props
 */
function ThreadedReplies(props) {
  const { replies: replyIds, enableEditing, replyEditId, isInbox, wizardProps, useCompression,
    inboxMessageId, myPresenceIsAssigned, enableActions, isDeletable} = props;

  const comments = React.useContext(LocalCommentsContext).comments;
  const classes = useThreadedReplyStyles();

  const replies = [];
  (replyIds || []).forEach((replyId) => {
    const aReply = comments.find(comment => comment.id === replyId);
    // If on Inbox might not be displaying all so don't freak if not found
    if (aReply) {
      replies.push(aReply);
    }
  });

  const sortedReplies = _.sortBy(replies, "created_at");
  if (useCompression) {
    const notifiedComment = comments.find((aComment) => aComment.id === inboxMessageId);
    return (
      <ol className={classes.container}>
        {sortedReplies.map((reply) => {
          if (reply) {
            if (reply.reply_id !== notifiedComment.reply_id || reply.id === inboxMessageId) {
              return (
                <ThreadedReply
                  className={classes.visible}
                  comment={reply}
                  key={`threadc${reply.id}`}
                  enableEditing={enableEditing}
                  enableActions={enableActions}
                  isDeletable={isDeletable}
                  replyEditId={replyEditId}
                  isInbox={isInbox}
                  inboxMessageId={inboxMessageId}
                  myPresenceIsAssigned={myPresenceIsAssigned}
                  wizardProps={wizardProps}
                />
              );
            }
          }
          return React.Fragment;
        })}
      </ol>
    );
  }

  return (
    <ol className={classes.container}>
      {sortedReplies.map((reply) => {
        if (reply) {
          return (
            <ThreadedReply
              className={classes.visible}
              comment={reply}
              key={`threadc${reply.id}`}
              enableEditing={enableEditing}
              enableActions={enableActions}
              isDeletable={isDeletable}
              replyEditId={replyEditId}
              inboxMessageId={inboxMessageId}
              isInbox={isInbox}
              myPresenceIsAssigned={myPresenceIsAssigned}
              wizardProps={wizardProps}
            />
          );
        }
        return React.Fragment;
      })}
    </ol>
  );
}

function ThreadedReply(props) {
  const { comment, enableEditing, messages, replyEditId, isInbox, inboxMessageId, wizardProps,
    myPresenceIsAssigned, enableActions, isDeletable } = props;
  return <Reply key={`c${comment.id}`} id={`c${comment.id}`} className={props.className} comment={comment}
                enableEditing={enableEditing} messages={messages} replyEditId={replyEditId}
                isInbox={isInbox} inboxMessageId={inboxMessageId} wizardProps={wizardProps}
                enableActions={enableActions} isDeletable={isDeletable}
                myPresenceIsAssigned={myPresenceIsAssigned} />;
}

function useCommenter(comment, presences) {
  return presences.find(presence => presence.id === comment.created_by);
}

export default Reply;
